import { useLingui } from "@lingui/react/macro";

import React from "react";
import {
  ModalBody,
  ModalFooter,
  ModalProps
} from "@silversea-ssc/ui-components-react";
import clsx from "clsx";
import Modal from "components/Modal/Modal";
import {
  FycCruisesTagContentFragment,
  FycPromosContentFragment,
  FycShipsContentFragment,
  FycThemedCruisesContentFragment
} from "types/graphql-types";
import { ApplyButton } from "../ApplyButton/ApplyButton";
import { DurationFilterList } from "../Filters/DurationFilter";
import { FeatureFilterList } from "../Filters/FeatureFilter";
import { PromoFilterList } from "../Filters/PromoFilter";
import { ShipFilterList } from "../Filters/ShipFilter";
import InlineToggleFilter from "../InlineToggleFilter";
import { SpecialTypeFilterMap } from "../utils";
import * as styles from "./ModalFilters.module.scss";
import SearchBar from "../SearchBar";
import { CruiseTagFilterList } from "components/FindYourCruise/Components/Filters/CruiseTagFilter";
import {
  isEnvFeatureEnabled,
  useClientSideFeatureFlag
} from "src/utils/feature-flags";

export type ModalFiltersProps = {
  showShipFilter?: boolean;
  showDurationFilter?: boolean;
  showPromosFilter?: boolean;
  showFeaturesFilter?: boolean;
  showCruiseEventsFilter?: boolean;
  showExpeditionFilter?: boolean;
  specialTypeFilterMapping: SpecialTypeFilterMap | undefined;
  excludePromosByConfigurationCodes?: number[];

  //content
  shipsContent: FycShipsContentFragment[];
  promosContent: FycPromosContentFragment[];
  themedCruisesContent: FycThemedCruisesContentFragment[];
  cruiseTagContent: FycCruisesTagContentFragment[];
  isVisible?: boolean;
};

type Props = ModalFiltersProps &
  Omit<ModalProps, "modal"> & {
    modal: Omit<ModalProps["modal"], "isOpen">;
  };

const ModalFilters: React.FC<Props> = ({
  modal,
  dataContentName,
  showShipFilter,
  showDurationFilter,
  showFeaturesFilter,
  showCruiseEventsFilter,
  showPromosFilter,
  showExpeditionFilter,
  specialTypeFilterMapping,
  shipsContent,
  promosContent,
  themedCruisesContent,
  cruiseTagContent,
  isVisible = false,
  excludePromosByConfigurationCodes
}) => {
  const { i18n, t } = useLingui();
  const withCruiseEventFilter = useClientSideFeatureFlag("cruiseTagFilter");

  return (
    <Modal
      title={t`Add filters`}
      modal={{
        ...modal,
        isOpen: true, // force modal to be always mounted. If unmounted the filters are resetted
        portalClassName: clsx({ [styles.hideModal]: !isVisible })
      }}
      size="fullscreen"
      dataContentName={dataContentName}
    >
      <ModalBody
        variant="wide"
        className={styles.body}
        paddingBottom
        removeScroll={isVisible}
      >
        <div className={styles.checkboxes}>
          {showExpeditionFilter && (
            <InlineToggleFilter
              id="expedition-checkbox"
              attribute="cruiseType"
              label={"Expedition Cruise"}
              value="Expedition"
              dataContentPiece="expedition_checkbox"
            />
          )}

          {specialTypeFilterMapping && (
            <InlineToggleFilter
              id="top-cruises-checkbox"
              attribute={specialTypeFilterMapping.key}
              label={i18n._(specialTypeFilterMapping.label)}
              value={specialTypeFilterMapping.value}
              dataContentPiece="top_cruises_checkbox"
            />
          )}

          <InlineToggleFilter
            id="available-checkbox"
            attribute="available"
            label={t`Available only`}
            value="true"
            dataContentPiece="available_checkbox"
          />
        </div>

        <div className={styles.filters}>
          {showDurationFilter && <DurationFilterList />}

          {showShipFilter && <ShipFilterList shipsContent={shipsContent} />}
          {showPromosFilter && (
            <PromoFilterList
              promosContent={promosContent}
              excludeConfigurationCodes={excludePromosByConfigurationCodes}
            />
          )}
          {showFeaturesFilter && !withCruiseEventFilter && (
            <FeatureFilterList themedCruisesContent={themedCruisesContent} />
          )}
          {showCruiseEventsFilter && withCruiseEventFilter && (
            <CruiseTagFilterList cruisesTagContent={cruiseTagContent} />
          )}
        </div>
        <div className={styles.search}>
          <SearchBar placeholder={t`Search voyage number`} />
        </div>
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <ApplyButton onClick={modal.onRequestClose} />
      </ModalFooter>
    </Modal>
  );
};

export default ModalFilters;
